<template>
  <div class="route-form">
    <!-- Свёрнутая полоска: маршрут построен и виден на карте -->
    <div
      v-if="collapsed"
      class="route-form__strip"
    >
      <button
        type="button"
        class="route-form__strip-body"
        @click="$emit('expand')"
      >
        <i class="exu-route" />
        <span class="route-form__strip-text">{{ from.text }} → {{ to.text }}</span>
      </button>
      <button
        type="button"
        class="route-form__icon-btn"
        :aria-label="$t('map.routeClose')"
        @click="$emit('close')"
      >
        <i class="exu-x-close" />
      </button>
    </div>

    <div
      v-else
      class="route-form__body"
    >
      <div class="route-form__head">
        <span class="route-form__title">{{ $t('map.route') }}</span>
        <button
          type="button"
          class="route-form__icon-btn"
          :aria-label="$t('map.routeClose')"
          @click="$emit('close')"
        >
          <i class="exu-x-close" />
        </button>
      </div>

      <div
        v-for="field in fields"
        :key="field.name"
        class="route-form__row"
      >
        <!--
          Метка ссылается на поле, а не оборачивает его: внутри блока
          рядом с полем стоят кнопки, а нажатие на кнопку внутри метки
          отдало бы фокус полю и подняло клавиатуру. Правило ругается на
          вычисляемый `for` — статического id у полей нет, они
          отрисованы циклом; по той же причине оно отключено в
          upInput.vue.
        -->
        <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
        <label
          class="route-form__label-text"
          :for="`route-${field.name}`"
        >{{ $t(field.label) }}</label>

        <!-- Кнопка, поле, кнопка — одним блоком: так видно, что
             боковые части тоже нажимаются -->
        <div class="route-form__field">
          <button
            type="button"
            class="route-form__side-btn"
            :aria-label="$t('map.pickOnMap')"
            @click="aimOnMap(field.name)"
          >
            <i class="exu-marker" />
          </button>

          <div class="route-form__control">
            <!-- input__wrapper — обёртка поля из upInput: её правила
                 действуют только на прямого потомка -->
            <div class="input__wrapper">
              <input
                :id="`route-${field.name}`"
                :value="field.model.text"
                type="text"
                class="up-form-control"
                :placeholder="$t('map.standNumber')"
                @input="onFieldInput(field.name, $event)"
              >
            </div>
            <button
              v-if="field.model.text"
              type="button"
              class="route-form__inline-btn"
              :aria-label="$t('map.clear')"
              @click="clearField(field.name)"
            >
              <i class="exu-x-close" />
            </button>
          </div>

          <button
            v-if="places.length"
            type="button"
            class="route-form__side-btn"
            :aria-label="$t('map.pickPlace')"
            @click="toggleList(field.name)"
          >
            <i :class="openList === field.name ? 'exu-chevron-up' : 'exu-chevron-down'" />
          </button>
        </div>
      </div>

      <div
        v-if="openList"
        class="route-form__places"
      >
        <ul>
          <li
            v-for="place in places"
            :key="place.id"
          >
            <button
              type="button"
              class="route-form__place"
              @click="pickPlace(place)"
            >
              {{ place.label }}
            </button>
          </li>
        </ul>
      </div>

      <p
        v-if="message"
        class="route-form__message"
      >
        {{ message }}
      </p>

      <div class="up-btn__line base-3-6-3">
        <button
          type="button"
          class="up-btn"
          :class="canBuild ? 'up-btn-accent' : 'up-btn-disabled'"
          :disabled="!canBuild"
          @click="build"
        >
          {{ $t('map.buildRoute') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { normalizeStandNumber } from '@/views/pages/MapView/standState';

export default {
  name: 'RouteFormView',

  props: {
    // цели без номера: кафе, WC, входы. Стенды сюда не попадают —
    // их полторы сотни, списком их не выбирают
    places: {
      type: Array,
      default: () => [],
    },
    // Значения полей держит страница, а не форма: в них пишет ещё и
    // прицел, а он живёт поверх карты, когда формы на экране нет.
    // Вид: { text, id }
    from: {
      type: Object,
      required: true,
    },
    to: {
      type: Object,
      required: true,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    message: {
      type: String,
      default: null,
    },
  },

  emits: ['build', 'close', 'expand', 'touch', 'aim', 'update:from', 'update:to'],

  data() {
    return {
      openList: null,
    };
  },

  computed: {
    fields() {
      return [
        { name: 'from', label: 'map.from', model: this.from },
        { name: 'to', label: 'map.to', model: this.to },
      ];
    },

    /**
     * Совпавшие концы маршрута не ошибка и сообщения не заслуживают —
     * поэтому кнопка просто не нажимается, и сообщать не о чем.
     */
    canBuild() {
      const from = this.from.text.trim();
      const to = this.to.text.trim();
      if (!from || !to) return false;
      if (this.from.id && this.to.id) return this.from.id !== this.to.id;
      return normalizeStandNumber(from) !== normalizeStandNumber(to);
    },
  },

  methods: {
    setField(name, value) {
      this.$emit(`update:${name}`, value);
      this.$emit('touch');
    },

    toggleList(name) {
      this.openList = this.openList === name ? null : name;
    },

    /**
     * Набранный текст отменяет выбор из списка: подпись больше не
     * относится к той цели, что была выбрана. Заодно гасит прошлое
     * сообщение — «Стенд 9X99 не значится» относится к тому, что было
     * введено, а не к тому, что вводят сейчас.
     */
    onFieldInput(name, event) {
      this.setField(name, { text: event.target.value, id: null });
    },

    pickPlace(place) {
      const name = this.openList === 'from' ? 'from' : 'to';
      this.setField(name, { text: place.label, id: place.id });
      this.openList = null;
    },

    clearField(name) {
      this.setField(name, { text: '', id: null });
    },

    aimOnMap(name) {
      this.openList = null;
      this.$emit('aim', name);
    },

    build() {
      this.openList = null;
      this.$emit('build', { from: { ...this.from }, to: { ...this.to } });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

// Вид собран из того же словаря, что и остальное приложение: кнопки —
// `up-btn` с модификаторами, поле — `input__wrapper` из upInput,
// список — как `droplist`. Своего здесь только раскладка.
.route-form {
  color: $color-text;
  background: $color-base;
  border-radius: $border-radius-inside-large;
  box-shadow: 0 0 10px 3px rgba(0, 0, 0, 0.25);

  &__body {
    display: flex;
    flex-direction: column;
    gap: $padding-step * 3;
    padding: $padding-step * 3 $padding-step * 3 $padding-step * 4;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 32px;
  }

  &__title {
    font-size: 1.1rem;
    font-weight: 600;
  }

  &__row {
    display: flex;
    flex-direction: column;
    gap: $padding-step / 2;
  }

  &__label-text {
    font-size: 0.8rem;
    color: $color-disabled;
  }

  // Кнопка, поле и кнопка живут одной рамкой: обводка всего блока и
  // разделители между частями. Иначе иконки по краям не читаются как
  // нажимаемые — на это и была жалоба.
  &__field {
    display: flex;
    align-items: stretch;
    // тот же цвет, что у рамки поля в upInput: блок её и заменяет
    border: 1px solid #e0e0e0;
    border-radius: $border-radius-inside-large;
    background: $color-base;
    overflow: hidden;
  }

  // Боковые части залиты, а середина белая: иконка на белом читается
  // как украшение — в строке поиска выше лупа и правда не кнопка.
  // Заливка отделяет нажимаемое от поля надёжнее, чем обводка: синяя
  // рамка вокруг всего блока читалась бы как «поле в фокусе».
  &__side-btn {
    flex: 0 0 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    border: none;
    background: $color-base-secondary;
    padding: 0;
    font-size: 22px;
    color: $color-primary;

    &:first-child {
      border-right: 1px solid #e0e0e0;
    }
    &:last-child {
      border-left: 1px solid #e0e0e0;
    }
    &:active {
      background: darken($color-base-secondary, 6%);
    }
  }

  &__control {
    position: relative;
    flex: 1 1 100%;
    min-width: 0;
  }

  // Рамка теперь у блока, поэтому у самого поля её нет
  &__control :deep(input.up-form-control) {
    border: none;
    border-radius: 0;
    padding-right: 40px;
  }

  &__inline-btn {
    position: absolute;
    top: 0;
    right: 0;
    width: 40px;
    height: 100%;
    border: none;
    background: transparent;
    padding: 0;
    font-size: 20px;
    color: $color-default;
  }

  &__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    padding: 0;
    font-size: 20px;
    color: $color-default;
  }

  // Список мест повторяет droplist: та же рамка, радиус и выделение
  &__places {
    border: 1px solid $color-base-secondary;
    border-radius: $border-radius-inside-large;
    overflow: hidden;

    ul {
      // В пикселях, а не в vh: панель не должна вырасти настолько,
      // чтобы вытеснить с экрана кнопки зума
      max-height: 176px;
      overflow-y: auto;
      margin: 0;
      padding: $padding-step 0;
      list-style: none;
    }
  }

  &__place {
    display: block;
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    padding: $padding-step * 2 $padding-step * 3;
    font-size: 1rem;
    color: $color-text;

    &:hover,
    &:focus {
      background: $color-base-secondary;
      color: $color-primary;
    }
  }

  &__message {
    margin: 0;
    font-size: 0.85rem;
    color: $color-text-danger;
  }

  // ─── свёрнутая полоска ───────────────────────────────────
  &__strip {
    display: flex;
    align-items: center;
    padding: $padding-step $padding-step * 2 $padding-step $padding-step;
  }

  &__strip-body {
    display: flex;
    align-items: center;
    gap: $padding-step * 2;
    flex: 1 1 100%;
    border: none;
    background: transparent;
    text-align: left;
    padding: $padding-step * 2;
    font-size: 1rem;
    color: $color-text;

    & > i {
      font-size: 22px;
      color: $color-primary;
    }
  }

  &__strip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
